import { CamaraVigilancia, type ModoGrabador } from '../../domain/entities/CamaraVigilancia';
import { traducirErrorSupabase } from '../../domain/errores';
import { env } from '../../config/env';
import { aBooleano, aFecha, aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';
import { leerNombres } from './consultas';

/** Grabador de vídeo (DVR, NVR o XVR) del que cuelgan las cámaras. */
export interface Grabador {
  id: number;
  nombre: string;
  modo: ModoGrabador;
  /** Modo automático: RTSP sin canal ni credenciales. */
  rtspBase: string | null;
  usuario: string | null;
  /** Modo manual: enlace ya publicado por el servidor, sin canal. */
  enlaceBase: string | null;
  clienteId: number | null;
  clienteNombre: string | null;
  areaId: number | null;
  areaNombre: string | null;
  activo: boolean;
  camaras: number;
  actualizadoEn: Date | null;
}

export interface DatosGrabador {
  nombre: string;
  modo: ModoGrabador;
  rtsp_base: string | null;
  usuario: string | null;
  /** Se envía sólo cuando se quiere cambiar; nunca se lee de vuelta. */
  clave?: string | null;
  enlace_base: string | null;
  cliente_id: number;
  area_id: number | null;
  activo: boolean;
}

export interface DatosCamara {
  grabador_id: number;
  nombre: string;
  canal: string;
  orden: number | null;
  activo: boolean;
  /** Se fija al crearla y no vuelve a cambiar. */
  camino?: string | null;
}

const TABLA_GRABADORES = 'cctv_grabadores';
const TABLA_CAMARAS = 'cctv_camaras';

/**
 * Columnas legibles del grabador.
 *
 * `clave` queda fuera a propósito: la base no concede permiso de lectura sobre
 * esa columna, así que pedirla haría fallar la consulta entera. La contraseña
 * del grabador se escribe desde aquí y sólo la lee el agente del servidor.
 */
const COLUMNAS_GRABADOR =
  'id, nombre, modo, rtsp_base, usuario, enlace_base, cliente_id, area_id, activo, actualizado_en';

/**
 * Grabadores y cámaras de videovigilancia.
 *
 * Las cámaras no guardan a qué cliente ni a qué área pertenecen: lo heredan de
 * su grabador. Por eso el listado se resuelve en dos pasos —primero los
 * grabadores que la sesión alcanza, después sus cámaras— en vez de con una
 * consulta anidada: así el ámbito se aplica en un solo sitio y no hay forma de
 * que una cámara se cuele por una ruta que no lo comprueba.
 */
export class RepositorioCctv {
  // ── Grabadores ────────────────────────────────────────────────────────────

  async listarGrabadores(clienteId?: number | null): Promise<Grabador[]> {
    const filas = await this.leerFilasGrabadores(clienteId);
    if (filas.length === 0) return [];

    const ids = filas.map((f) => aNumero(f.id) ?? 0).filter((id) => id !== 0);

    const [clientes, areas, conteo] = await Promise.all([
      leerNombres(
        'clientes',
        filas.map((f) => aNumero(f.cliente_id)).filter((id): id is number => id !== null),
      ),
      leerNombres(
        'areas',
        filas.map((f) => aNumero(f.area_id)).filter((id): id is number => id !== null),
      ),
      this.contarCamaras(ids),
    ]);

    return filas
      .map((fila) => {
        const id = aNumero(fila.id) ?? 0;
        const clienteDeFila = aNumero(fila.cliente_id);
        const areaDeFila = aNumero(fila.area_id);

        return {
          id,
          nombre: aTexto(fila.nombre) ?? 'Sin nombre',
          modo: aTexto(fila.modo) === 'manual' ? ('manual' as const) : ('automatico' as const),
          rtspBase: aTexto(fila.rtsp_base),
          usuario: aTexto(fila.usuario),
          enlaceBase: aTexto(fila.enlace_base),
          clienteId: clienteDeFila,
          clienteNombre: clienteDeFila !== null ? (clientes.get(clienteDeFila) ?? null) : null,
          areaId: areaDeFila,
          areaNombre: areaDeFila !== null ? (areas.get(areaDeFila) ?? null) : null,
          activo: aBooleano(fila.activo) ?? true,
          camaras: conteo.get(id) ?? 0,
          actualizadoEn: aFecha(fila.actualizado_en),
        };
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  async crearGrabador(datos: DatosGrabador): Promise<void> {
    const { error } = await supabase.from(TABLA_GRABADORES).insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizarGrabador(id: number, datos: Partial<DatosGrabador>): Promise<void> {
    const { error } = await supabase.from(TABLA_GRABADORES).update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminarGrabador(id: number): Promise<void> {
    const { error } = await supabase.from(TABLA_GRABADORES).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async cambiarActivoGrabador(id: number, activo: boolean): Promise<void> {
    await this.actualizarGrabador(id, { activo });
  }

  // ── Cámaras ───────────────────────────────────────────────────────────────

  /**
   * Cámaras que la sesión puede ver, con los datos de su grabador ya resueltos.
   * Es lo que consume tanto la pantalla de vistas como la parrilla en vivo.
   */
  async listarCamaras(clienteId?: number | null): Promise<CamaraVigilancia[]> {
    const grabadores = await this.listarGrabadores(clienteId);
    if (grabadores.length === 0) return [];

    const porId = new Map(grabadores.map((g) => [g.id, g]));

    const { data, error } = await supabase
      .from(TABLA_CAMARAS)
      .select('id, grabador_id, nombre, canal, orden, activo, camino')
      .in('grabador_id', [...porId.keys()]);

    if (error) throw traducirErrorSupabase(error);

    const camaras: CamaraVigilancia[] = [];

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const grabadorId = aNumero(fila.grabador_id);
      const grabador = grabadorId !== null ? porId.get(grabadorId) : undefined;
      if (!grabador) continue;

      camaras.push(
        new CamaraVigilancia({
          id: aNumero(fila.id) ?? 0,
          nombre: aTexto(fila.nombre) ?? 'Sin nombre',
          canal: aTexto(fila.canal) ?? '',
          orden: aNumero(fila.orden),
          activa: aBooleano(fila.activo) ?? true,
          camino: aTexto(fila.camino),
          grabadorId: grabador.id,
          grabadorNombre: grabador.nombre,
          grabadorActivo: grabador.activo,
          modo: grabador.modo,
          rtspBase: grabador.rtspBase,
          enlaceBase: grabador.enlaceBase,
          baseDeVideo: env.baseDeVideo,
          clienteId: grabador.clienteId,
          clienteNombre: grabador.clienteNombre,
          areaId: grabador.areaId,
          areaNombre: grabador.areaNombre,
        }),
      );
    }

    return camaras.sort(CamaraVigilancia.comparar);
  }

  async crearCamara(datos: DatosCamara): Promise<void> {
    const { error } = await supabase.from(TABLA_CAMARAS).insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizarCamara(id: number, datos: Partial<DatosCamara>): Promise<void> {
    const { error } = await supabase.from(TABLA_CAMARAS).update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminarCamara(id: number): Promise<void> {
    const { error } = await supabase.from(TABLA_CAMARAS).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async cambiarActivoCamara(id: number, activo: boolean): Promise<void> {
    await this.actualizarCamara(id, { activo });
  }

  // ── Interno ───────────────────────────────────────────────────────────────

  private async leerFilasGrabadores(clienteId?: number | null): Promise<Record<string, unknown>[]> {
    let consulta = ambitoSesion.aplicar(
      filtroDemo.aplicar(
        TABLA_GRABADORES,
        supabase.from(TABLA_GRABADORES).select(COLUMNAS_GRABADOR),
      ),
    );

    if (clienteId) consulta = consulta.eq('cliente_id', clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    return (data ?? []) as Record<string, unknown>[];
  }

  /** Cuántas cámaras cuelgan de cada grabador. */
  private async contarCamaras(grabadorIds: readonly number[]): Promise<Map<number, number>> {
    const mapa = new Map<number, number>();
    if (grabadorIds.length === 0) return mapa;

    const { data, error } = await supabase
      .from(TABLA_CAMARAS)
      .select('grabador_id')
      .in('grabador_id', [...grabadorIds]);

    if (error) throw traducirErrorSupabase(error);

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const id = aNumero(fila.grabador_id);
      if (id !== null) mapa.set(id, (mapa.get(id) ?? 0) + 1);
    }

    return mapa;
  }
}

export const repositorioCctv = new RepositorioCctv();
